import { forwardRef, useCallback } from 'react';
import {
  Link as RouterLink,
  NavLink as RouterNavLink,
  useNavigate as useRouterNavigate,
} from 'react-router-dom';
import type { LinkProps, NavLinkProps, NavigateOptions, To } from 'react-router-dom';

import { localize } from './paths';
import { useStore } from './store';

/**
 * Ссылки и переходы, которые сами держат язык.
 *
 * Страницы пишут «/tea/x», а на русской версии ссылка ведёт на «/ru/tea/x».
 * Без этого каждую ссылку пришлось бы собирать руками и легко забыть одну.
 */

function useLocalize(): (to: To) => To {
  const { lang } = useStore();
  return useCallback(
    (to: To) => (typeof to === 'string' && to.startsWith('/') ? localize(to, lang) : to),
    [lang],
  );
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link({ to, ...rest }, ref) {
  const loc = useLocalize();
  return <RouterLink ref={ref} to={loc(to)} {...rest} />;
});

export const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(function NavLink(
  { to, ...rest },
  ref,
) {
  const loc = useLocalize();
  return <RouterNavLink ref={ref} to={loc(to)} {...rest} />;
});

export function useNavigate(): (to: To, options?: NavigateOptions) => void {
  const navigate = useRouterNavigate();
  const loc = useLocalize();
  return useCallback((to: To, options?: NavigateOptions) => navigate(loc(to), options), [navigate, loc]);
}
